import { useState, useEffect, useRef } from 'react';
import { useStore, Person } from '@/store';
import { 
  formatTime, 
  getCurrentHourInZone, 
  getWorkStatus, 
  getStatusEmoji,
  convertWorkHours 
} from '@/lib/timezone';
import { Trash2, Maximize2, Minimize2 } from 'lucide-react';
import { ActivityDot } from './ActivityIndicator';
import { trackAction } from '@/lib/analytics';
import { isCustomAvatar } from '@/lib/avatars';

// Extended person type that includes 'me' source
type TimelinePerson = Person | {
  id: string;
  name: string;
  avatar: string;
  timezone: string;
  location: string;
  workHourStart: number;
  workHourEnd: number;
  source: 'me';
  activityStatus?: 'online' | 'away' | 'offline' | null;
  lastSeen?: number;
};

interface TimelineViewProps {
  className?: string;
}

export default function TimelineView({ className = '' }: TimelineViewProps) {
  const { me, people, settings, removePerson } = useStore();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Check if night mode is active based on document class
  const isNightShift = document.documentElement.classList.contains('dark');
  
  // Update time every second
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  
  // Handle fullscreen toggle
  const handleFullscreen = async () => {
    if (!containerRef.current) return;
    
    try {
      if (!document.fullscreenElement) {
        await containerRef.current.requestFullscreen?.();
        setIsFullscreen(true);
        trackAction('enter_fullscreen');
      } else {
        await document.exitFullscreen();
        setIsFullscreen(false);
        trackAction('exit_fullscreen');
      }
    } catch (err) {
      console.error('Fullscreen error:', err);
    }
  };
  
  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);
  
  // Current hour in my timezone
  const myCurrentHour = getCurrentHourInZone(me.timezone);
  
  // Convert everyone's work hours to my timezone
  const allPeople = [
    { ...me, id: 'me', source: 'me' as const },
    ...people,
  ];

  return (
    <div 
      ref={containerRef}
      className={`${className} transition-all duration-500 ${
        isFullscreen 
          ? 'min-h-screen bg-bg-light dark:bg-bg flex flex-col justify-center p-12' 
          : `rounded-lg border border-gray-100 dark:border-white/5 bg-white/50 dark:bg-bg-card/50 backdrop-blur-sm p-6 ${isNightShift ? 'shadow-primary/10' : 'shadow-sm'}`
      }`}
    >
      {/* Screensaver Mode: Large Time Display */}
      {isFullscreen && (
        <div className="text-center mb-12 animate-in fade-in duration-1000">
          <div className="text-7xl md:text-9xl font-bold font-mono tracking-tight text-primary mb-2">
            {formatTime(currentTime, me.timezone, { use24Hour: settings.use24Hour, showSeconds: false })}
          </div>
          <div className="text-2xl md:text-3xl text-text-light-muted dark:text-text-muted mb-1">
            {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
          </div>
          <div className="text-xl text-text-light-dim dark:text-text-dim">
            {me.timezone.replace('_', ' ')}
          </div>
        </div>
      )}
      
      <div className={`flex items-center justify-between mb-4 ${
        isFullscreen ? 'opacity-0 hover:opacity-100 transition-opacity duration-300 absolute top-4 right-4 z-50' : 'justify-end'
      }`}>
        {/* Fullscreen Toggle Only */}
        <button
          onClick={handleFullscreen}
          className={`transition-all flex items-center gap-2 ${
            isFullscreen 
              ? 'bg-primary hover:bg-primary-hover text-white px-6 py-3 rounded-lg shadow-2xl font-medium' 
              : 'text-text-light-muted dark:text-text-muted hover:text-primary dark:hover:text-primary px-3 py-1.5 text-sm font-medium'
          }`}
          title={isFullscreen ? 'Exit screensaver (or press ESC)' : 'Enter fullscreen wallpaper mode'}
        >
          {isFullscreen ? (
            <>
              <Minimize2 className="w-4 h-4" />
              <span>Exit Wallpaper</span>
            </>
          ) : (
            <>
              <Maximize2 className="w-4 h-4" />
              <span>Wallpaper Mode</span>
            </>
          )}
        </button>
      </div>
      
      {/* Timeline Header - always show in fullscreen */}
      <div className="relative mb-2">
        <div className={`flex justify-between px-12 ${
          isFullscreen 
            ? 'text-text-light-muted dark:text-text-dim text-base font-medium mb-4' 
            : 'text-text-light-muted dark:text-text-dim text-xs'
        }`}>
          {[0, 3, 6, 9, 12, 15, 18, 21, 24].map((hour) => (
            <span key={hour} style={{ width: '1px' }}>{hour.toString().padStart(2, '0')}</span>
          ))}
        </div>
        
        {/* NOW marker */}
        {!isFullscreen && (
          <div 
            className="absolute top-0 text-primary font-medium text-xs"
            style={{ left: `calc(${(myCurrentHour / 24) * 100}% + 48px - 15px)` }}
          >
            ▼ NOW
          </div>
        )}
      </div>
      
      {/* Timeline Rows - adaptive spacing based on team size */}
      <div className={(() => {
        const peopleCount = allPeople.length;
        if (isFullscreen) {
          // Fullscreen: more compact for larger teams
          if (peopleCount >= 7) return 'space-y-4'; // Compact
          if (peopleCount >= 4) return 'space-y-6'; // Normal
          return 'space-y-8'; // Comfortable
        } else {
          // Normal mode
          if (peopleCount >= 7) return 'space-y-2'; // Compact
          return 'space-y-3'; // Normal
        }
      })()}>
        {allPeople.map((person) => (
          <TimelineRow
            key={person.id}
            person={person}
            myTimezone={me.timezone}
            currentTime={currentTime}
            settings={settings}
            isMe={person.id === 'me'}
            isNightShift={isNightShift}
            isFullscreen={isFullscreen}
            peopleCount={allPeople.length}
            onRemove={person.id !== 'me' ? () => removePerson(person.id) : undefined}
          />
        ))}
      </div>
      
      {/* Legend - minimal, only shown if needed */}
      {!isFullscreen && people.length > 0 && (
        <div className="mt-4 pt-3 border-t border-gray-200 dark:border-white/10 flex flex-wrap gap-3 text-xs text-text-light-muted dark:text-text-muted opacity-60">
        <div className="flex items-center gap-1">
          <div className="w-3 h-1.5 bg-working/30 rounded" />
          <span>Work</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-0.5 h-3 bg-primary rounded" />
          <span>Now</span>
        </div>
        </div>
      )}
      
      {/* Exit hint in fullscreen */}
      {isFullscreen && (
        <div className="text-center mt-12 text-text-light-dim dark:text-text-dim text-sm animate-pulse">
          Press ESC or click the button to exit
        </div>
      )}
    </div>
  );
}

interface TimelineRowProps {
  person: TimelinePerson;
  myTimezone: string;
  currentTime: Date;
  settings: { use24Hour: boolean; showSeconds: boolean };
  isMe: boolean;
  isNightShift: boolean;
  isFullscreen: boolean;
  onRemove?: () => void;
}

function TimelineRow({ person, myTimezone, currentTime, settings, isMe, isNightShift, isFullscreen, onRemove, peopleCount = 1 }: TimelineRowProps & { peopleCount?: number }) {
  // Convert person's work hours to my timezone
  const [workStartInMyTz, workEndInMyTz] = convertWorkHours(
    person.workHourStart,
    person.workHourEnd,
    person.timezone,
    myTimezone
  );
  
  // Get person's current hour in their timezone
  const theirCurrentHour = getCurrentHourInZone(person.timezone);
  const status = getWorkStatus(theirCurrentHour, person.workHourStart, person.workHourEnd);
  const statusEmoji = getStatusEmoji(status);
  
  // Their time string
  const theirTimeStr = formatTime(currentTime, person.timezone, {
    use24Hour: settings.use24Hour,
    showSeconds: false,
  });
  
  // Work zone position (may wrap around midnight)
  const workStartPercent = (workStartInMyTz / 24) * 100;
  const workEndPercent = (workEndInMyTz / 24) * 100;
  const wrapsAround = workStartInMyTz > workEndInMyTz;

  return (
    <div className={`group ${isMe ? 'bg-primary/5 -mx-4 px-4 py-3 rounded-lg' : ''}`}>
      {/* Top: Info Section (Name, Time, Status) */}
      <div className="flex items-center justify-between mb-2">
        {/* Left: Avatar + Name */}
        <div className="flex items-center gap-2 min-w-0">
          <div className={`rounded-full overflow-hidden flex items-center justify-center flex-shrink-0 ${(() => {
            if (isFullscreen) {
              if (peopleCount >= 7) return 'w-12 h-12'; // Compact
              if (peopleCount >= 4) return 'w-16 h-16'; // Normal
              return 'w-20 h-20'; // Comfortable
            }
            return 'w-10 h-10';
          })()}`}>
            {isCustomAvatar(person.avatar) ? (
              <img src={person.avatar} alt={person.name} className="w-full h-full object-cover" />
            ) : (
              <img src={person.avatar} alt={person.name} className="w-full h-full" />
            )}
          </div>
          <div className={`font-medium flex items-center gap-1.5 truncate text-text-light dark:text-text ${(() => {
            if (isFullscreen) {
              if (peopleCount >= 7) return 'text-xl'; // Compact
              if (peopleCount >= 4) return 'text-2xl'; // Normal
              return 'text-3xl'; // Comfortable
            }
            return 'text-base';
          })()}`}>
            {isMe ? 'You' : person.name}
            {/* Activity status dot for shared (real) users */}
            {'activityStatus' in person && person.activityStatus && (
              <ActivityDot status={person.activityStatus} size={isFullscreen ? 'md' : 'sm'} />
            )}
          </div>
        </div>
        
        {/* Right: Time + Status Emoji + Remove Button */}
        <div className="flex items-center gap-3">
          <div className={`text-text-light dark:text-text flex items-center gap-2 font-mono ${(() => {
            if (isFullscreen) {
              if (peopleCount >= 7) return 'text-base'; // Compact
              if (peopleCount >= 4) return 'text-xl'; // Normal
              return 'text-2xl'; // Comfortable
            }
            return 'text-sm';
          })()}`}>
            <span>{theirTimeStr}</span>
            <span className={(() => {
              if (isFullscreen) {
                if (peopleCount >= 7) return 'text-2xl'; // Compact
                if (peopleCount >= 4) return 'text-3xl'; // Normal
                return 'text-4xl'; // Comfortable
              }
              return 'text-xl';
            })()}>{statusEmoji}</span>
          </div>
          
          {/* Remove button - hide in fullscreen */}
          {onRemove && !isFullscreen && (
            <button
              onClick={onRemove}
              className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-500/20 rounded text-red-400 transition-opacity"
              title="Remove"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      
      {/* Bottom: Timeline Bar */}
      <div className="relative w-full">
        <div className={`rounded-full relative overflow-hidden transition-colors w-full border ${(() => {
          if (isFullscreen) {
            if (peopleCount >= 7) return 'h-10'; // Compact
            if (peopleCount >= 4) return 'h-12'; // Normal  
            return 'h-16'; // Comfortable
          }
          return 'h-8';
        })()} ${
          isNightShift 
            ? 'bg-slate-900 border-slate-700' 
            : 'bg-gray-200 dark:bg-bg border-gray-300 dark:border-white/10'
        }`}>
          {/* Work hours zone */}
          {wrapsAround ? (
            <>
              <div 
                className={`absolute top-0 bottom-0 border-y transition-colors ${
                  isNightShift
                    ? 'bg-amber-600/40 border-amber-500/60'
                    : 'bg-working/20 border-working/40'
                }`}
                style={{ left: `${workStartPercent}%`, right: 0 }}
              />
              <div 
                className={`absolute top-0 bottom-0 border-y transition-colors ${
                  isNightShift
                    ? 'bg-amber-600/40 border-amber-500/60'
                    : 'bg-working/20 border-working/40'
                }`}
                style={{ left: 0, width: `${workEndPercent}%` }}
              />
            </>
          ) : (
            <div 
              className={`absolute top-0 bottom-0 border-y transition-colors ${
                isNightShift
                  ? 'bg-amber-600/40 border-amber-500/60'
                  : 'bg-working/20 border-working/40'
              }`}
              style={{ 
                left: `${workStartPercent}%`, 
                width: `${workEndPercent - workStartPercent}%` 
              }}
            />
          )}
          
          {/* Current time marker */}
          <div 
            className={`absolute rounded-full shadow-lg transition-colors ${
              isFullscreen ? 'top-2 bottom-2 w-2' : 'top-1 bottom-1 w-1.5'
            } ${
              isNightShift
                ? 'bg-yellow-400'
                : isMe ? 'bg-primary' : 'bg-text'
            }`}
            style={{ left: `calc(${(theirCurrentHour / 24) * 100}% - ${isFullscreen ? '4px' : '3px'})` }}
          />
          
          {/* Hour grid lines */}
          {[6, 12, 18].map((hour) => (
            <div
              key={hour}
              className="absolute top-0 bottom-0 w-px bg-white/10"
              style={{ left: `${(hour / 24) * 100}%` }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
